import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Shield, LogIn } from 'lucide-react';
import { useAuth } from '../contexts/Authcontext';
import { useTheme } from '../contexts/Themecontext';
import { authAPI } from '../services/api';
//import logo from '../../assets/img/logo.png';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login(email, password, otp || null);

      if (response.error === '2FA code required') {
        setRequires2FA(true);
        setLoading(false);
        return;
      }

      const { user, access_token, refresh_token } = response;

      // Check if user is admin
      if (user.role !== 'admin') {
        setError('Access denied. Admin credentials required.');
        setLoading(false);
        return;
      }
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      login(user, access_token);
      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Admin login error:', err);
      
      if (err.response?.data?.requires_2fa) {
        setRequires2FA(true);
      } else {
        setError(err.response?.data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gray-50',
        card: 'bg-white',
        border: 'border-gray-200',
        text: 'text-gray-900',
        textMuted: 'text-gray-600',
        input: 'bg-white border-gray-300 text-gray-900 focus:border-purple-500',
        button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-slate-900',
        card: 'bg-slate-800',
        border: 'border-slate-700',
        text: 'text-white',
        textMuted: 'text-gray-400',
        input: 'bg-slate-900 border-slate-700 text-white focus:border-purple-500',
        button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-purple-950 to-pink-950',
      card: 'bg-slate-900/50 backdrop-blur-xl',
      border: 'border-purple-500/30',
      text: 'text-white',
      textMuted: 'text-gray-300',
      input: 'bg-slate-900/50 border-purple-500/30 text-white focus:border-purple-400',
      button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500',
    };
  };

  const classes = getThemeClasses();

  return (
    <div className={`min-h-screen ${classes.bg} flex items-center justify-center px-4 py-12 pt-20 sm:px-6 lg:px-8`}>
      {theme === 'quantum' && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-purple-500 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      )}

      <div className="relative max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 p-1">
              <div className="w-full h-full bg-slate-900 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-purple-400" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-1 mb-2">
            <span className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              GLYQIX
            </span>
            <span className={`text-3xl font-bold ${classes.textMuted}`}>
              Admin
            </span>
          </div>
          <h2 className={`text-3xl font-bold ${classes.text}`}>Admin Portal</h2>
          <p className={`mt-2 ${classes.textMuted}`}>Sign in to manage the platform</p>
        </div>

        <div className={`${classes.card} border ${classes.border} rounded-2xl shadow-2xl p-8`}>
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm">
              {error}
            </div>
          )}

          {requires2FA && (
            <div className="mb-4 p-4 bg-purple-500/10 border border-purple-500/50 rounded-lg text-purple-400 text-sm">
              Please enter your 2FA code
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                Admin Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className={`w-5 h-5 ${classes.textMuted}`} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={requires2FA}
                  className={`block w-full pl-10 pr-3 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2 transition-colors disabled:opacity-50`}
                  placeholder="admin@glyqix.com"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`w-5 h-5 ${classes.textMuted}`} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={requires2FA}
                  className={`block w-full pl-10 pr-10 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2 transition-colors disabled:opacity-50`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={requires2FA}
                >
                  {showPassword ? (
                    <EyeOff className={`w-5 h-5 ${classes.textMuted}`} />
                  ) : (
                    <Eye className={`w-5 h-5 ${classes.textMuted}`} />
                  )}
                </button>
              </div>
            </div>

            {requires2FA && (
              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>
                  2FA Code
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Shield className={`w-5 h-5 ${classes.textMuted}`} />
                  </div>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    maxLength={6}
                    className={`block w-full pl-10 pr-3 py-3 border ${classes.input} rounded-lg focus:outline-none focus:ring-2 transition-colors`}
                    placeholder="123456"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-semibold rounded-lg ${classes.button} shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  Admin Sign In
                </>
              )}
            </button>

            {requires2FA && (
              <button
                type="button"
                onClick={() => {
                  setRequires2FA(false);
                  setOtp('');
                  setError('');
                }}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
              >
                Back to login
              </button>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className={`text-sm ${classes.textMuted}`}>
              Need admin access?{' '}
              <Link to="/admin/request-access" className="font-medium text-purple-400 hover:text-purple-300 transition-colors">
                Request Access
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/login" className={`text-sm ${classes.textMuted} hover:text-purple-400 transition-colors`}>
              ← Back to User Login
            </Link>
          </div>
        </div>

        <div className="text-center">
          <p className={`text-xs ${classes.textMuted}`}>
            <Shield className="w-3 h-3 inline mr-1" />
            Secure admin access with enhanced protection
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;